import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import withRoleAccess from '../../hoc/withRoleAccess';
import { useTranslation } from 'react-i18next';
import Sidebar from '../../components/Sidebar/Sidebar';
import Header from '../../components/Header/Header';
import {
  Card,
  Table,
  Typography,
  Space,
  Tag,
  Button,
  message,
  Row,
  Col,
  Drawer,
  Alert,
  Layout,
  Form,
  Select,
  DatePicker,
  Input,
  Tabs,
  Progress,
  Divider
} from 'antd';
import {
  CalendarOutlined,
  PauseOutlined,
  ExclamationCircleOutlined,
  CheckCircleOutlined,
  HourglassOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';
import moment from 'moment';
import getUserIdFromToken from '../../Utils/authUtils';

const { Text } = Typography;
const { Content } = Layout;
const { Option } = Select;
const { TabPane } = Tabs;

function StudentLeaveRequest() {
  const { t } = useTranslation();
  const userId = getUserIdFromToken();
  const [classes, setClasses] = useState([]);
  const [leaveRequests, setLeaveRequests] = useState([]);
  const [pauseRequests, setPauseRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [leaveModalVisible, setLeaveModalVisible] = useState(false);
  const [pauseModalVisible, setPauseModalVisible] = useState(false);
  const [leaveForm] = Form.useForm();
  const [pauseForm] = Form.useForm();
  
  const API_URL = process.env.REACT_APP_API_URL;

  // Fetch student's classes
 // Replace your existing fetchStudentClasses function with this:

const fetchStudentClasses = useCallback(async () => {
  setLoading(true);
  try {
    const token = localStorage.getItem('token');
    if (!token || !userId) {
      message.error(t('Please log in again.'));
      return;
    }

    const response = await axios.get(`${API_URL}/registrations/classes/${userId}`, {
      headers: { Authorization: `Bearer ${token}` }
    });

    const allClasses = response.data.classes || [];
    const today = dayjs();
    
    // Group classes by class_code
    const classesByCode = {};
    allClasses.forEach(cls => {
      if (!classesByCode[cls.class_code]) {
        classesByCode[cls.class_code] = [];
      }
      classesByCode[cls.class_code].push(cls);
    });

    // Include all classes from courses that have at least one active class
    const activeClasses = [];
    Object.values(classesByCode).forEach(classGroup => {
      // Check if any class in this group is still active
      const hasActiveClass = classGroup.some(cls => {
        const endDate = dayjs(cls.end_date);
        return endDate.isAfter(today) || endDate.isSame(today, 'day');
      });
      
      // If the course has any active classes, include ALL classes from this course
      if (hasActiveClass) {
        activeClasses.push(...classGroup);
      }
    });

    setClasses(activeClasses);
  } catch (error) {
    console.error('Error fetching classes:', error);
    message.error(t('Error fetching classes: ') + (error.response?.data?.message || t('Server error')));
  } finally {
    setLoading(false);
  }
}, [API_URL, userId, t]);
  // Fetch leave requests
  const fetchLeaveRequests = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/leave-requests/${userId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLeaveRequests(response.data || []);
    } catch (error) {
      console.error('Error fetching leave requests:', error);
    }
  }, [API_URL, userId]);

  // Fetch pause requests
  const fetchPauseRequests = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/leave-requests/pause-requests/${userId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPauseRequests(response.data || []);
    } catch (error) {
      console.error('Error fetching pause requests:', error);
    }
  }, [API_URL, userId]);

  useEffect(() => {
    fetchStudentClasses();
    fetchLeaveRequests();
    fetchPauseRequests();
  }, [fetchStudentClasses, fetchLeaveRequests, fetchPauseRequests]);

  // Debug function to understand data structure
  const debugClassData = useCallback(() => {
    
    // Group by class_code to see the issue
    const byClassCode = {};
    classes.forEach(cls => {
      if (!byClassCode[cls.class_code]) {
        byClassCode[cls.class_code] = [];
      }
      byClassCode[cls.class_code].push({
        classid: cls.classid,
        classgroup_id: cls.classgroup_id,
        subject_name: cls.subject_name,
        date: cls.date
      });
    });
    
    
    // Show unique classgroup_ids
    const uniqueGroupIds = [...new Set(classes.map(cls => cls.classgroup_id))];
    
    // Show which classes have the same class_code but different classgroup_id
    Object.keys(byClassCode).forEach(classCode => {
      const uniqueGroupIds = [...new Set(byClassCode[classCode].map(cls => cls.classgroup_id))];
      if (uniqueGroupIds.length > 1) {
        console.warn(`⚠️ ${classCode} has multiple classgroup_ids:`, uniqueGroupIds);
      }
    });
  }, [classes]);

  // Call debug function when classes are loaded
  useEffect(() => {
    if (classes.length > 0) {
      debugClassData();
    }
  }, [classes, debugClassData]);


const leaveQuotaByClassCode = useMemo(() => {
  console.log('🔄 Recalculating leave quotas...');
  
  if (classes.length === 0) {
    console.log('⚠️ No classes found, returning empty quota');
    return {};
  }

  const groupStats = {};
  
  // Step 1: Group classes by class_code
  classes.forEach((cls, index) => {
    const groupKey = cls.class_code;
    
    if (!groupStats[groupKey]) {
      groupStats[groupKey] = {
        totalClasses: 0,
        usedLeaves: 0,
        maxLeaves: 0,
        className: cls.class_code,
        subjects: new Set(),
        allClasses: [],
        classgroupIds: new Set(),
        classgroup_id: cls.classgroup_id // Keep the first one for reference
      };
    }
    
    groupStats[groupKey].allClasses.push(cls);
    groupStats[groupKey].classgroupIds.add(cls.classgroup_id);
    groupStats[groupKey].subjects.add(cls.subject_name);
  });

  // Step 2: Calculate totals for each group
  Object.keys(groupStats).forEach(groupKey => {
    const group = groupStats[groupKey];
    
    // Count total classes
    group.totalClasses = group.allClasses.length;
    
    // Calculate max leaves (2 per 10 classes)
    group.maxLeaves = Math.floor(group.totalClasses / 10) * 2;
    
    // Convert subjects Set to string
    group.subjectName = Array.from(group.subjects).join(', ');
    
    console.log(`📊 Group ${groupKey}:`, {
      totalClasses: group.totalClasses,
      maxLeaves: group.maxLeaves,
      classgroupIds: Array.from(group.classgroupIds)
    });
  });

  // Step 3: Calculate used leaves
  console.log('🔍 Calculating used leaves from requests:', leaveRequests.length);
  
  leaveRequests.forEach((request, index) => {
    console.log(`📋 Processing leave request ${index + 1}:`, {
      id: request.id,
      classgroup_id: request.classgroup_id,
      status: request.status,
      original_date: request.original_date
    });
    
    if (request.status === 'approved') {
      // Find which group this request belongs to
      let groupFound = false;
      Object.keys(groupStats).forEach(groupKey => {
        const group = groupStats[groupKey];
        if (group.classgroupIds.has(request.classgroup_id)) {
          group.usedLeaves += 1;
          groupFound = true;
          console.log(`   ✅ Added to group ${groupKey}, new count: ${group.usedLeaves}`);
        }
      });
      
      if (!groupFound) {
        console.log(`   ⚠️ No group found for classgroup_id: ${request.classgroup_id}`);
      }
    } else {
      console.log(`   ⏭️ Skipping non-approved request (status: ${request.status})`);
    }
  });

  // Step 4: Clean up and log final results
  Object.keys(groupStats).forEach(groupKey => {
    const group = groupStats[groupKey];
    delete group.subjects;
    delete group.allClasses;
    delete group.classgroupIds; // Remove after calculation
    
    console.log(`✅ Final quota for ${groupKey}:`, {
      usedLeaves: group.usedLeaves,
      maxLeaves: group.maxLeaves,
      totalClasses: group.totalClasses
    });
  });

  return groupStats;
}, [classes, leaveRequests]);

// Add this useEffect to log when the calculation changes
useEffect(() => {
  console.log('📈 Leave quota calculation updated:', leaveQuotaByClassCode);
}, [leaveQuotaByClassCode]);

// Add this debugging function to check why some classes are missing

const debugMissingClasses = useCallback(() => {
  console.log('=== DEBUGGING MISSING CLASSES ===');
  
  // Filter Math5 classes specifically
  const math5Classes = classes.filter(cls => cls.class_code === 'Math5');
  console.log('Math5 classes found in array:', math5Classes.length);
  
  // Group by subject to see what's included
  const bySubject = {};
  math5Classes.forEach(cls => {
    if (!bySubject[cls.subject_name]) {
      bySubject[cls.subject_name] = [];
    }
    bySubject[cls.subject_name].push({
      date: cls.date,
      classgroup_id: cls.classgroup_id,
      classid: cls.classid
    });
  });
  
  console.log('Math5 classes grouped by subject:', bySubject);
  
  // Check the date filtering in fetchStudentClasses
  const allMath5BeforeFiltering = classes.filter(cls => cls.class_code === 'Math5');
  const today = dayjs();
  
  allMath5BeforeFiltering.forEach(cls => {
    const endDate = dayjs(cls.end_date);
    const isActive = endDate.isAfter(today) || endDate.isSame(today, 'day');
    console.log(`Class ${cls.classid} (${cls.subject_name}) - End: ${cls.end_date}, Active: ${isActive}`);
  });
  
  // Show unique classgroup_ids for Math5
  const uniqueClassgroupIds = [...new Set(math5Classes.map(cls => cls.classgroup_id))];
  console.log('Math5 unique classgroup_ids:', uniqueClassgroupIds);
  
}, [classes]);

// Add this to your useEffect
useEffect(() => {
  if (classes.length > 0) {
    debugClassData();
    debugMissingClasses(); // Add this line
  }
}, [classes, debugClassData, debugMissingClasses]);

  // Handle leave request submission
  const handleLeaveSubmit = async (values) => {
    try {
      const token = localStorage.getItem('token');
      const selectedClass = classes.find(cls => cls.classid === values.classid);
      
      const requestData = {
        student_id: userId,
        classgroup_id: selectedClass.classgroup_id,
        class_id: values.classid,
        original_date: moment(selectedClass.date).format('YYYY-MM-DD'),
        original_time: selectedClass.schedule_time,  
        preferred_date: values.preferred_date.format('YYYY-MM-DD'),
        preferred_time: values.preferred_time?.format 
          ? values.preferred_time.format('HH:mm:ss') 
          : values.preferred_time,
        reason: values.reason
      };

      await axios.post(`${API_URL}/leave-requests`, requestData, {
        headers: { Authorization: `Bearer ${token}` }
      });

      message.success(t('Leave request submitted successfully'));
      setLeaveModalVisible(false);
      leaveForm.resetFields();
      fetchLeaveRequests();
    } catch (error) {
      console.error('Error submitting leave request:', error);
      message.error(t('Error submitting leave request: ') + (error.response?.data?.message || t('Server error')));
    }
  };

  // Handle pause request submission
  const handlePauseSubmit = async (values) => {
    try {
      const token = localStorage.getItem('token');

      const requestData = {
        student_id: userId,
        classgroup_id: values.classgroup_id,
        pause_type: values.pause_type,
        planned_duration: values.planned_duration,
        resume_date: values.resume_date.format('YYYY-MM-DD'),
        reason: values.reason
      };

      await axios.post(`${API_URL}/leave-requests/pause-requests`, requestData, {
        headers: { Authorization: `Bearer ${token}` }
      });

      message.success(t('Pause request submitted successfully'));
      setPauseModalVisible(false);
      pauseForm.resetFields();
      fetchPauseRequests();
    } catch (error) {
      console.error('❌ Error submitting pause request:', error);
      message.error(t('Error submitting pause request: ') + (error.response?.data?.message || t('Server error')));
    }
  };

  // Check if leave request is allowed based on time constraints
  const isLeaveRequestAllowed = (classDate) => {
    const now = dayjs();
    const classDayBefore = dayjs(classDate).subtract(1, 'day').hour(18).minute(30).second(0);
    return now.isBefore(classDayBefore);
  };

  // Check consecutive leave requests for a classgroup
  const getConsecutiveLeaves = useCallback((classgroupId) => {
    const groupLeaves = leaveRequests
      .filter(req => req.classgroup_id === classgroupId && req.status === 'approved')
      .sort((a, b) => dayjs(a.original_date).unix() - dayjs(b.original_date).unix());
      
    if (groupLeaves.length < 2) return 0;
    
    let consecutive = 0;
    let maxConsecutive = 0;
    
    for (let i = 1; i < groupLeaves.length; i++) {
      const prevDate = dayjs(groupLeaves[i-1].original_date);
      const currentDate = dayjs(groupLeaves[i].original_date);
      
      // Check if classes are consecutive (within 2 weeks of each other)
      if (currentDate.diff(prevDate, 'days') <= 14) {
        consecutive++;
        maxConsecutive = Math.max(maxConsecutive, consecutive + 1);
      } else {
        consecutive = 0;
      }
    }
    
    return maxConsecutive;
  }, [leaveRequests]);

  // Get future classes for leave selection
  const futureClasses = useMemo(() => {
    const today = dayjs();
    return classes.filter(cls => dayjs(cls.date).isAfter(today));
  }, [classes]);

  // Check if leave can be requested for a class (updated to use class_code)
  const canRequestLeave = (classItem) => {
    const quota = leaveQuotaByClassCode[classItem.class_code]; // Changed to use class_code
    const hasQuota = quota && quota.usedLeaves < quota.maxLeaves;
    
    // Check time constraint (before 18:30 of previous day)
    const timeAllowed = isLeaveRequestAllowed(classItem.date);
    
    // Check consecutive leaves (max 2 consecutive, 3rd forces pause)
    const consecutiveLeaves = getConsecutiveLeaves(classItem.classgroup_id);
    const consecutiveAllowed = consecutiveLeaves < 2;
    
    return hasQuota && timeAllowed && consecutiveAllowed;
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'approved': return 'green';
      case 'rejected': return 'red';
      case 'pending': return 'orange';
      default: return 'default';
    }
  };

  const leaveColumns = [
    {
      title: t('Course/Subject'),
      dataIndex: 'class_code',
      key: 'class_code',
      render: (text, record) => (
        <Space direction="vertical" size={0}>
          <Tag color="blue">{text}</Tag>
          <Text type="secondary" style={{ fontSize: '12px' }}>
            {record.subject_name} ({record.class_id})
          </Text>
        </Space>
      ),
    },
    {
      title: t('Original Date/Time'),
      key: 'original',
      render: (_, record) => (
        <Space direction="vertical" size={0}>
          <Text>{dayjs(record.original_date).format('DD MMM YYYY')}</Text>
          <Text type="secondary">
            {dayjs(record.original_time, 'HH:mm:ss').format('HH:mm')}
          </Text>
        </Space>
      ),
    },
    {
      title: t('Preferred Date/Time'),
      key: 'preferred',
      render: (_, record) => (
        <Space direction="vertical" size={0}>
          <Text>{dayjs(record.preferred_date).format('DD MMM YYYY')}</Text>
          <Text type="secondary">
            {dayjs(record.preferred_time, 'HH:mm:ss').format('HH:mm')}
          </Text>
        </Space>
      ),
    },
    {
      title: t('Status'),
      dataIndex: 'status',
      key: 'status',
      render: (status) => (
        <Tag color={getStatusColor(status)} icon={
          status === 'approved' ? <CheckCircleOutlined /> : 
          status === 'pending' ? <HourglassOutlined /> : 
          <ExclamationCircleOutlined />
        }>
          {status?.toUpperCase()}
        </Tag>
      ),
    },
    {
      title: t('Requested Date'),
      dataIndex: 'created_at',
      key: 'created_at',
      render: (date) => dayjs(date).format('DD MMM YYYY'),
    },
    {
      title: t('Admin Notes'),
      dataIndex: 'admin_notes',
      key: 'admin_notes',
      render: (notes) => <Text>{notes || ''}</Text>,
    }
  ];

  const pauseColumns = [
    {
      title: t('Course'),
      dataIndex: 'class_code',
      key: 'class_code',
      render: (text, record) => (
        <Space direction="vertical" size={0}>
          <Tag color="purple">{text}</Tag>
        </Space>
      ),
    },
    {
      title: t('Pause Type'),
      dataIndex: 'pause_type',
      key: 'pause_type',
      render: (type) => (
        <Tag color="orange">{type?.replace('_', ' ').toUpperCase()}</Tag>
      ),
    },
    {
      title: t('Duration'),
      dataIndex: 'planned_duration',
      key: 'planned_duration',
    },
    {
      title: t('Resume Date'),
      dataIndex: 'resume_date',
      key: 'resume_date',
      render: (date) => dayjs(date).format('DD MMM YYYY'),
    },
    {
      title: t('Status'),
      dataIndex: 'status',
      key: 'status',
      render: (status) => (
        <Tag color={getStatusColor(status)} icon={
          status === 'approved' ? <CheckCircleOutlined /> : 
          status === 'pending' ? <HourglassOutlined /> : 
          <ExclamationCircleOutlined />
        }>
          {status?.toUpperCase()}
        </Tag>
      ),
    },
    {
      title: t('Admin Notes'),
      dataIndex: 'admin_notes',
      key: 'admin_notes',
      render: (notes) => <Text>{notes || ''}</Text>,
    }
  ];

  return (
    <div>
      <Sidebar />
      <Header />

      <Content className="performance-content">
        {/* Leave Quota Summary - Updated to use class_code grouping */}
        <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
          {Object.entries(leaveQuotaByClassCode).map(([classCode, quota]) => (
            <Col xs={24} sm={12} md={8} key={classCode}>
              <Card size="small">
                <Space direction="vertical" style={{ width: '100%' }}>
                  <Text strong>{quota.className}</Text>
                  <Text type="secondary" style={{ fontSize: '12px' }}>
                    {quota.subjectName}
                  </Text>
                  <Progress
                    percent={quota.maxLeaves > 0 ? (quota.usedLeaves / quota.maxLeaves) * 100 : 0}
                    format={() => `${quota.usedLeaves}/${quota.maxLeaves}`}
                    status={quota.usedLeaves >= quota.maxLeaves ? 'exception' : 'active'}
                  />
                  <Text style={{ fontSize: '12px' }}>
                    Leave quota based on {quota.totalClasses} total classes
                  </Text>
                </Space>
              </Card>
            </Col>
          ))}
        </Row>

        <Tabs 
          defaultActiveKey="requests"
          tabPosition={window.innerWidth < 768 ? 'top' : 'top'}
          size={window.innerWidth < 768 ? 'small' : 'default'}
        >
          <TabPane 
            tab={
              <span>
                <CalendarOutlined />
                <span style={{ marginLeft: window.innerWidth < 576 ? 4 : 8 }}>
                  {window.innerWidth < 576 ? t('Leave') : t('Leave Requests')}
                </span>
              </span>
            } 
            key="requests"
          >
            <Card
              title={
                <div style={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'center',
                  flexWrap: 'wrap',
                  gap: 8
                }}>
                  <span style={{ fontSize: window.innerWidth < 576 ? '14px' : '16px' }}>
                    {window.innerWidth < 576 ? t('Leave Requests') : t('Class Leave Requests')}
                  </span>
                  <Button 
                    type="primary" 
                    icon={<CalendarOutlined />}
                    onClick={() => setLeaveModalVisible(true)}
                    disabled={futureClasses.length === 0}
                    size={window.innerWidth < 576 ? 'small' : 'default'}
                  >
                    <span style={{ display: window.innerWidth < 480 ? 'none' : 'inline' }}>
                      {t('Request Leave')}
                    </span>
                  </Button>
                </div>
              }
              bodyStyle={{ padding: window.innerWidth < 768 ? '12px' : '24px' }}
            >
              <Table
                columns={leaveColumns}
                dataSource={leaveRequests}
                rowKey="id"
                loading={loading}
                scroll={{ x: 'max-content' }}
                style={{ width: '100%', overflowX: 'auto' }}
                pagination={{
                  pageSize: window.innerWidth < 768 ? 5 : 10,
                  showSizeChanger: window.innerWidth >= 768,
                  showQuickJumper: window.innerWidth >= 768,
                  simple: window.innerWidth < 576,
                  showTotal: window.innerWidth >= 768 ? (total, range) =>
                    `${range[0]}-${range[1]} of ${total} requests` : false,
                }}
              />
            </Card>
          </TabPane>

          <TabPane 
            tab={
              <span>
                <PauseOutlined />
                <span style={{ marginLeft: window.innerWidth < 576 ? 4 : 8 }}>
                  {window.innerWidth < 576 ? t('Pause') : t('Pause Requests')}
                </span>
              </span>
            } 
            key="pause"
          >
            <Card
              title={
                <div style={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'center',
                  flexWrap: 'wrap',
                  gap: 8
                }}>
                  <span style={{ fontSize: window.innerWidth < 576 ? '14px' : '16px' }}>
                    {window.innerWidth < 576 ? t('Pause Requests') : t('Course Pause Requests')}
                  </span>
                  <Button 
                    type="primary" 
                    icon={<PauseOutlined />}
                    onClick={() => setPauseModalVisible(true)}
                    size={window.innerWidth < 576 ? 'small' : 'default'}
                  >
                    <span style={{ display: window.innerWidth < 480 ? 'none' : 'inline' }}>
                      {t('Request Pause')}
                    </span>
                  </Button>
                </div>
              }
              bodyStyle={{ padding: window.innerWidth < 768 ? '12px' : '24px' }}
            >
              <Table
                columns={pauseColumns}
                dataSource={pauseRequests}
                rowKey="id"
                loading={loading}
                scroll={{ x: 'max-content' }}
                style={{ width: '100%', overflowX: 'auto' }}
                pagination={{
                  pageSize: window.innerWidth < 768 ? 5 : 10,
                  showSizeChanger: window.innerWidth >= 768,
                  showQuickJumper: window.innerWidth >= 768,
                  simple: window.innerWidth < 576,
                  showTotal: window.innerWidth >= 768 ? (total, range) =>
                    `${range[0]}-${range[1]} of ${total} requests` : false,
                }}
              />
            </Card>
          </TabPane>
        </Tabs>

        {/* Leave Request Drawer */}
        <Drawer
          title={<><CalendarOutlined /> {t('Request Class Leave')}</>}
          open={leaveModalVisible}
          onClose={() => setLeaveModalVisible(false)}
          width={window.innerWidth < 768 ? '100%' : 600}
          placement="right"
        >
          <Alert
            message={t('Leave Policy & Guidelines')}
            description={
              <div style={{ fontSize: '12px', lineHeight: '1.4' }}>
                <div style={{ marginBottom: 8 }}>
                  <Text strong>{t('Leave Entitlements')}:</Text>
                  <ul style={{ margin: '4px 0', paddingLeft: 16 }}>
                    <li>{t('1 class per week: 1 leave per month')}</li>
                    <li>{t('2 classes per week: 2 leaves per month')}</li>
                    <li>{t('3 classes per week: 3 leaves per month')}</li>
                  </ul>
                </div>
                
                <div style={{ marginBottom: 8 }}>
                  <Text strong>{t('Important Rules')}:</Text>
                  <ul style={{ margin: '4px 0', paddingLeft: 16 }}>
                    <li>{t('Notify staff at least 1 day in advance during office hours (10:00 AM - 6:30 PM)')}</li>
                    <li>{t('Emergency leave on class day: Institute may conduct 1 hour of regular class')}</li>
                    <li>{t('Leave within 2 hours before class: Institute may conduct full regular class')}</li>
                    <li>{t('Private group classes: All group members must be notified')}</li>
                    <li>{t('Group classes: Leave not allowed')}</li>
                    <li>{t('3 consecutive leave sessions: Schedule may be cancelled and rearranged')}</li>
                  </ul>
                </div>
              </div>
            }
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
          />

          {futureClasses.some(cls => !isLeaveRequestAllowed(cls.date)) && (
            <Alert
              message={t('Time Restriction Notice')}
              description={t('Leave requests must be submitted before 18:30 of the day before your class. Classes starting tomorrow cannot be requested for leave.')}
              type="warning"
              showIcon
              style={{ marginBottom: 16 }}
            />
          )}

          {futureClasses.some(cls => getConsecutiveLeaves(cls.classgroup_id) >= 2) && (
            <Alert
              message={t('Consecutive Leave Limit')}
              description={t('You have taken 2 consecutive leaves. For the 3rd consecutive absence, please use the Pause Course option instead.')}
              type="error"
              showIcon
              style={{ marginBottom: 16 }}
              action={
                <Button 
                  type="link" 
                  onClick={() => {
                    setLeaveModalVisible(false);
                    setPauseModalVisible(true);
                  }}
                >
                  {t('Request Pause Instead')}
                </Button>
              }
            />
          )}

          <Form
            form={leaveForm}
            layout="vertical"
            onFinish={handleLeaveSubmit}
          >
            <Form.Item
              name="classid"
              label={t('Select Class to Take Leave')}
              rules={[{ required: true, message: t('Please select a class') }]}
            >
              <Select 
                placeholder={t('Select a future class')}
                optionLabelProp="label"
                style={{ width: '100%' }}
              >
                {futureClasses.map(cls => (
                  <Option 
                    key={cls.classid} 
                    value={cls.classid}
                    disabled={!canRequestLeave(cls)}
                    label={`${cls.class_code} | ${cls.subject_name} (${dayjs(cls.date).format('DD MMM YYYY')})`}
                  >
                    <Space direction="vertical" size={0}>
                      <Text strong>{cls.class_code} - {cls.subject_name} (ID:{cls.classid})</Text>
                      <Text type="secondary">
                        {dayjs(cls.date).format('DD MMM YYYY')} at {moment(cls.schedule_time, 'HH:mm:ss').format('HH:mm')}
                      </Text>

                      {!canRequestLeave(cls) && (
                        <Text type="danger" style={{ fontSize: '12px' }}>
                          {!leaveQuotaByClassCode[cls.class_code] || leaveQuotaByClassCode[cls.class_code].usedLeaves >= leaveQuotaByClassCode[cls.class_code].maxLeaves
                            ? t('No leave quota available')
                            : !isLeaveRequestAllowed(cls.date)
                            ? t('Too late to request leave (after 18:30 previous day)')
                            : getConsecutiveLeaves(cls.classgroup_id) >= 2
                            ? t('2 consecutive leaves taken - must use Pause instead')
                            : t('Leave not available')
                          }
                        </Text>
                      )}
                    </Space>
                  </Option>
                ))}
              </Select>
            </Form.Item>

            <Divider>{t('Preferred Make-up Class')}</Divider>

            <Row gutter={[16, 16]}>
              <Col xs={24} sm={12}>
                <Form.Item
                  name="preferred_date"
                  label={t('Preferred Date')}
                  rules={[{ required: true, message: t('Please select preferred date') }]}
                >
                  <DatePicker 
                    style={{ width: '100%' }}
                    disabledDate={(current) => current && current < dayjs().endOf('day')}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12}>
                <Form.Item
                  name="preferred_time"
                  label={t('Preferred Time')}
                  rules={[{ required: true, message: t('Please select preferred time') }]}
                >
                  <Select 
                    style={{ width: '100%' }}
                    placeholder={t('Select time')}
                  >
                    {Array.from({ length: 23 }, (_, i) => {
                      const hour = 8 + Math.floor(i / 2);
                      const minute = i % 2 === 0 ? '00' : '30';
                      const timeString = `${hour.toString().padStart(2, '0')}:${minute}`;
                      
                      if (hour > 19) return null;
                      
                      return (
                        <Option key={timeString} value={timeString}>
                          {timeString}
                        </Option>
                      );
                    }).filter(Boolean)}
                  </Select>
                </Form.Item>
              </Col>
            </Row>

            <Form.Item
              name="reason"
              label={t('Reason for Leave')}
              rules={[{ required: true, message: t('Please provide a reason') }]}
            >
              <Input.TextArea rows={4} placeholder={t('Please explain why you need to take leave...')} />
            </Form.Item>

            <Form.Item>
              <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
                <Button onClick={() => setLeaveModalVisible(false)}>
                  {t('Cancel')}
                </Button>
                <Button type="primary" htmlType="submit">
                  {t('Submit Request')}
                </Button>
              </Space>
            </Form.Item>
          </Form>
        </Drawer>

        {/* Pause Request Drawer */}
        <Drawer
          title={<><PauseOutlined /> {t('Request Course Pause')}</>}
          open={pauseModalVisible}
          onClose={() => setPauseModalVisible(false)}
          width={window.innerWidth < 768 ? '100%' : 600}
          placement="right"
        >
          <Alert
            message={t('Course Break & Pause Policy')}
            description={
              <div style={{ fontSize: '12px', lineHeight: '1.4' }}>
                <div style={{ marginBottom: 12 }}>
                  <Text>{t('Keaes Academics students are most successful when they study consistently. However, situations may occur which cause a student to be away from the program for an extended period of time.')}</Text>
                </div>
                
                <div style={{ marginBottom: 8 }}>
                  <Text strong>1. {t('Short Break (≤3 weeks)')}</Text>
                  <div style={{ marginLeft: 12, marginTop: 4 }}>
                    {t('We can reserve your spot on your teacher\'s schedule')}
                  </div>
                </div>
                
                <div style={{ marginBottom: 8 }}>
                  <Text strong>2. {t('Long Break (>3 weeks)')}</Text>
                  <div style={{ marginLeft: 12, marginTop: 4 }}>
                    {t('Schedule will be rearranged. We cannot guarantee the same teacher but will try our best. Classes will be put on waiting list.')}
                  </div>
                </div>
                
                <div style={{ marginBottom: 12 }}>
                  <Text strong>3. {t('Pause Course (≥6 weeks)')}</Text>
                  <div style={{ marginLeft: 12, marginTop: 4 }}>
                    <ul style={{ margin: '4px 0', paddingLeft: 16 }}>
                      <li>{t('Enrollment preserved for maximum 365 days from pause date')}</li>
                      <li>{t('Teacher and schedule will NOT be reserved')}</li>
                      <li>{t('Must submit request at least 3 days before pause begins')}</li>
                    </ul>
                  </div>
                </div>
                
                <div style={{ background: '#fff2e8', padding: 8, borderRadius: 4, border: '1px solid #ffbb96' }}>
                  <Text strong style={{ color: '#d46b08' }}>{t('Important Warnings')}:</Text>
                  <ul style={{ margin: '4px 0', paddingLeft: 16, color: '#d46b08' }}>
                    <li>{t('Course expires in 90 days if form not submitted within 15 days after pause')}</li>
                    <li>{t('Course terminates without notice if not resumed on stated date')}</li>
                    <li>{t('Course marked incomplete after 365 days - remaining time forfeited')}</li>
                    <li>{t('All courses are non-refundable')}</li>
                  </ul>
                </div>
              </div>
            }
            type="warning"
            showIcon
            style={{ marginBottom: 16 }}
          />

          <Form
            form={pauseForm}
            layout="vertical"
            onFinish={handlePauseSubmit}
          >
            <Form.Item
              name="classgroup_id"
              label={t('Select Course to Pause')}
              rules={[{ required: true, message: t('Please select a course') }]}
            >
              <Select placeholder={t('Select course')}>
                {[...new Set(classes.map(cls => cls.classgroup_id))].map(groupId => {
                  const cls = classes.find(c => c.classgroup_id === groupId);
                  return (
                    <Option key={groupId} value={groupId}>
                      {cls.class_code}
                    </Option>
                  );
                })}
              </Select>
            </Form.Item>

            <Form.Item
              name="pause_type"
              label={t('Pause Type')}
              rules={[{ required: true, message: t('Please select pause type') }]}
            >
              <Select placeholder={t('Select pause type')}>
                <Option value="short_break">{t('Short Break (3-8 weeks)')}</Option>
                <Option value="long_break">{t('Long Break (2-6 months)')}</Option>
                <Option value="pause_course">{t('Pause Course (6+ months)')}</Option>
              </Select>
            </Form.Item>

            <Form.Item
              name="planned_duration"
              label={t('When are you planning to resume?')}
              rules={[{ required: true, message: t('Please select planned duration') }]}
            >
              <Select placeholder={t('Select duration')}>
                <Option value="1-3 months">{t('1-3 months')}</Option>
                <Option value="4-6 months">{t('4-6 months')}</Option>
                <Option value="6-9 months">{t('6-9 months')}</Option>
                <Option value="9-12 months">{t('9-12 months')}</Option>
              </Select>
            </Form.Item>

            <Form.Item
              name="resume_date"
              label={t('Expected Resume Date')}
              rules={[{ required: true, message: t('Please select resume date') }]}
            >
              <DatePicker 
                style={{ width: '100%' }}
                disabledDate={(current) => current && current < dayjs().add(21, 'day')}
              />
            </Form.Item>

            <Form.Item
              name="reason"
              label={t('Reason for Pause')}
              rules={[{ required: true, message: t('Please provide a reason') }]}
            >
              <Input.TextArea rows={4} placeholder={t('Please explain why you need to pause your course...')} />
            </Form.Item>

            <Form.Item>
              <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
                <Button onClick={() => setPauseModalVisible(false)}>
                  {t('Cancel')}
                </Button>
                <Button type="primary" htmlType="submit">
                  {t('Submit Request')}
                </Button>
              </Space>
            </Form.Item>
          </Form>
        </Drawer>
      </Content>
    </div>
  );
}

export default withRoleAccess(StudentLeaveRequest, ['student']);